import { compact, get } from "lodash"
import { percentageFormatter } from "helpers/formatters/numbers"
import { getResidualAttributionSummarySubquery, RESIDUAL_RETURN } from "helpers/residualAttribution"

export const TOTAL_RETURN = "totalReturn"
export const ALPHA_RETURN = "alphaReturn"
export const FACTORS_RETURN = "factorsReturn"
export const TRADING_RETURN = "tradingReturn"
export { RESIDUAL_RETURN }

const BASE_METRICS = {
  [TOTAL_RETURN]: {
    label: "Total Return",
    path: "totalReturn.cumulativePercent",
    formatter: percentageFormatter,
  },
  [ALPHA_RETURN]: {
    label: "Alpha",
    path: "attribution.summary.specific.cumulativePercent",
    formatter: percentageFormatter,
  },
  [FACTORS_RETURN]: {
    label: "Factors",
    path: "attribution.summary.factors.cumulativePercent",
    formatter: percentageFormatter,
  },
  [TRADING_RETURN]: {
    label: "Trading",
    path: "attribution.summary.trading.cumulativePercent",
    formatter: percentageFormatter,
  },
}

const RESIDUAL_METRIC = {
  label: "Residual",
  path: "attribution.summary.residual.cumulativePercent",
  formatter: percentageFormatter,
}

export function getMetrics(isRfrAttributionEnabled = false) {
  return {
    ...BASE_METRICS,
    ...isRfrAttributionEnabled && { [RESIDUAL_RETURN]: RESIDUAL_METRIC },
  }
}

function getPerformanceSubquery(isRfrAttributionEnabled) {
  return `performance {
        date
        totalReturn {
          cumulativePercent
        }
        attribution {
          summary {
            factors {
              cumulativePercent
            }
            specific {
              cumulativePercent
            }
            trading {
              cumulativePercent
            }
            ${isRfrAttributionEnabled ? getResidualAttributionSummarySubquery() : ""}
          }
          factors {
            id
            category
            name
            return {
              cumulativePercent
            }
          }
        }
      }`
}

function buildQuery(isRfrAttributionEnabled, shouldUseIntradayQuery = false) {
  const performanceSubquery = getPerformanceSubquery(isRfrAttributionEnabled)

  if (shouldUseIntradayQuery) {
    return `#multi-date-cumulative-performance
query ($modelId: String!, $positionSet: PositionSetInput!, $on: Date!, $batchId: Id) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, on: $on, batchId: $batchId) {
      ${performanceSubquery}
    }
  }
}`
  }

  return `#multi-date-cumulative-performance
query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      ${performanceSubquery}
    }
  }
}`
}

export function createPerformanceCumulativeMultiDateQuery(modelId, positionSet, base, from, to, isIntradayMode, options = {}) {
  const shouldUseIntradaySimulation = isIntradayMode && from === to && !options.batchId
  const shouldNullBase = isIntradayMode && !shouldUseIntradaySimulation
  const { isRfrAttributionEnabled = false, intradayPriceRefreshTime } = options
  const key = compact(
    [
      "cumulativePerformance",
      "multiDate",
      modelId,
      positionSet,
      from,
      to,
      shouldUseIntradaySimulation,
      isRfrAttributionEnabled,
      ...(shouldUseIntradaySimulation || shouldNullBase ? [] : [base]),
      ...(intradayPriceRefreshTime ? [intradayPriceRefreshTime] : []),
    ])

  const variables = shouldUseIntradaySimulation ? {
    modelId,
    positionSet,
    on: from,
    batchId: options.batchId,
  } : {
    modelId,
    positionSet,
    base: shouldNullBase ? null : base,
    from,
    to,
  }

  return {
    key,
    query: buildQuery(isRfrAttributionEnabled, shouldUseIntradaySimulation),
    variables,
    select: (graphqlResult) => {
      const performance = get(graphqlResult, "data.model.simulation.performance", [])
      const metricsConfig = getMetrics(isRfrAttributionEnabled)

      return {
        values: {
          formatter: percentageFormatter,
          // The code to map the dates data deliberately does not use lodash functions to improve the performance
          dates: performance.map(({ date, ...dateData }) => {
            const metrics = Object.entries(metricsConfig).reduce((acc, [metricId, metric]) => {
              acc[metricId] = get(dateData, metric.path)
              return acc
            }, {})

            const factors = dateData.attribution.factors.reduce((acc, { id, category, return: { cumulativePercent } }) => {
              acc[id] = cumulativePercent
              acc[category] = (acc[category] ?? 0) + cumulativePercent
              return acc
            }, {})

            return {
              date,
              ...metrics,
              ...factors,
            }
          }),
        },
      }
    },
  }
}
